import {
	forwardRef,
	ReactElement,
	Ref,
	RefObject,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState
} from "react";
import {
	EmailEvents,
	EmailIdWithIsReadStatus,
	publish,
	subscribe
} from "client_legacy/Email/emailEvents";
import {
	ExchangeEmailId,
	ExchangeEmailManagementErrorModel,
	ExchangeFolderId,
	MailMessageLocationQueryModel,
} from "client/Api/Email/common";
import {
	deleteExchangeEmailByIds,
	EmailActions,
	EmailListItemModel,
	EmailScreenModel,
	IFolderViewModel,
	FolderCounters,
	GetEmailChangesRequest,
	getNewEmailsForFolderFromExchange,
	MailFolderLocationModel,
	markAllAsRead,
	markAllAsUnread,
	MessageFlagState,
	MessageReadState,
	moveEmailToFolder,
	setExchangeFlagStatus,
	setExchangeReadStatus,
	SignatureModel,
	LazyLoadedChildren
} from "client/Api/Email/email";
import {
	EMPTY_ARRAY,
	ensureNotNil,
	isNil,
	isNotNil,
	setTrackingId
} from "../../../Misc/utils";
import { EmailSidebar } from "../Sidebar/EmailSidebar";
import { EmailContent } from "../EmailContent/EmailContent";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { Poller } from "client_legacy/ndaw/poller";
import {
	BusyIndicator,
	ContextActionBar,
	FloatingActionButton,
	FormattedText
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { userConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { EmailNotConfiguredDialog } from "../Dialogs/EmailNotConfiguredDialog";
import { IntegrationCredentialsLinkKind } from "client/Api/IntegrationCredentials/integrationCredentials";
import classNames from "classnames";
import {
	DraftEmailModel,
	prepareExchangeEmailForForward,
	prepareExchangeEmailForReply,
	prepareExchangeEmailForReplyAll
} from "client/Api/Email/respondToEmail";
import { never, UnreachableCaseError } from "client_legacy/ndaw/errors";
import {
	AlertManager,
	MediaQueryKind,
	useAlerts,
	useUnmountedCancellation,
	useUserTimezone,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { HttpRequestError } from "client/Api/httpClient";
import styles from "./EmailPage.module.less";
import {
	CompositeActionContext,
	EmailActionBarOptions,
	useEmailActionBar
} from "./useEmailActionBar";
import { MailboxesManagementSidebar } from "../MailboxesManagement/MailboxesManagementSidebar";
import { SubscriptionTokenCollection } from "client_legacy/ndaw/subscriptionTokenCollection";
import { FolderMovedData, useMoveEmailsToFolderDialog } from "../Dialogs/MoveEmailsToFolderDialog";
import { Guid } from "client_legacy/ndaw/guid";
import { useSignatureStore } from "../Signatures/signatureStore";
import { useSignatureSettingsDialog } from "../Signatures/SignatureSettingsDialog/SignatureSettingsDialog";
import { useUploadAttachmentInProgressDialog } from "../Dialogs/UploadAttachmentInProgressDialog";
import { ListView, ListViewHandle } from "../ListView/ListView";
import { EmailTreeView } from "../Treeview/EmailTreeView";
import { SelectMode } from "client/Misc/commonTypes";
import { EmailActionModel, EmailSelectionActionContext } from "../Actions/selectionActions";
import { EmailComposerHandle } from "../EmailContent/EmailComposer/EmailComposer";
import { offerPasswordUpdateWhenPossibleChanged } from "client/Features/Settings/IntegrationCredentials/SyncPassword";
import { TimeZone } from "client/DateAndTime/timezones";
import { formatForDto } from "client/DateAndTime/dateTimeUtils";
import { ActionsWithContext } from "client_legacy/ndaw/actions";
import { SelectedFolderWithMailbox } from "../Email";
import {
	ExchangeAccountItem,
	ExchangeFolderItem,
	isFolderUnderDeleteFolder
} from "../common";

export type EmailPageProps = {
	screenModel: EmailScreenModel;
	accountItems: readonly ExchangeAccountItem[];
	sessionState: EmailSessionState;
	selectedFolderWithMailbox: SelectedFolderWithMailbox | undefined;
	onReloadScreenModel: () => void;
	onComposeNewEmail: () => void;
	onOpenResponseDraft: (
		draftId: string,
		responseKind: EmailResponseKind,
		originalEmailId: ExchangeEmailId,
		smtpAddress: string | null
	) => void;
	onSwitchToNoSession: () => void;
	onOpenEmail: (emailId: string, isForEditing: boolean) => void;
	onOpenFolder: (smtpAddress: string | null, folderId: string) => void;
	onNavigateToFolder: (smtpAddress: string, folderId: string) => void;
	onAccountsChange: (accounts: readonly ExchangeAccountItem[]) => void;
	onFetchFolders: (
		smtpAddress: string,
		folderId: string,
		shouldExcludeSelf: boolean
	) => Promise<readonly ExchangeAccountItem[]>;
};

export type EmailPageHandle = {
	notifyAboutDeletedEmails(emails: readonly EmailIdWithIsReadStatus[]): void;
};

export enum EmailSessionStateKind {
	None = 0,
	NewEmail = 1,
	EditDraft = 2,
	ViewEmail = 3
}

export type NoneEmailSessionState = {
	kind: EmailSessionStateKind.None;
};

export type CreateNewEmailSessionState = {
	kind: EmailSessionStateKind.NewEmail;
	to?: string | null;
	cc?: string | null;
	bcc?: string | null;
	sessionId: Guid;
};

export type ViewEmailSessionState = {
	kind: EmailSessionStateKind.ViewEmail;
	emailId: ExchangeEmailId;
	smtpAddress: string | null;
};

export type EmailResponseSessionState = {
	kind: EmailResponseKind;
	originalEmailId: ExchangeEmailId;
};

export type EditDraftEmailSessionState = {
	kind: EmailSessionStateKind.EditDraft;
	emailId: ExchangeEmailId;
	smtpAddress: string | null;
	doesRepresentNewlyGeneratedDraft: boolean;
	response?: EmailResponseSessionState;
	sessionId: Guid;
};

export type EmailSessionState =
	| NoneEmailSessionState
	| CreateNewEmailSessionState
	| ViewEmailSessionState
	| EditDraftEmailSessionState;

export const EmailPage = forwardRef(
	(props: EmailPageProps, ref: Ref<EmailPageHandle>): ReactElement => {
		const {
			screenModel,
			accountItems,
			sessionState,
			selectedFolderWithMailbox,
			onReloadScreenModel,
			onComposeNewEmail,
			onOpenResponseDraft,
			onSwitchToNoSession,
			onOpenEmail,
			onOpenFolder,
			onNavigateToFolder,
			onAccountsChange,
			onFetchFolders
		} = props;

		const {
			millisecondsBetweenRetrievingEmailChanges,
			showGoToOutlookAction,
			defaultComposerFontStyle,
			availableFroms,
			automaticallyShowSenderImages,
			useIntegrationCredentials
		} = screenModel;

		const [canShowEmptyMessage, setCanShowEmptyMessage] = useState<boolean>(false);
		const [isMailboxesManagementSidebarVisible, setIsMailboxesManagementSidebarVisible] = useState<boolean>(false);
		const watermarkRef: RefObject<string | null> = useRef<string>(null);
		const [counters, setCounters] = useState<Map<string, FolderCounters>>(new Map());

		const [integrationCredentialsCheckState, setIntegrationCredentialsCheckState] = useState<IntegrationCredentialsCheckState>(
			useIntegrationCredentials
			? IntegrationCredentialsCheckState.CheckIsRequired
			: IntegrationCredentialsCheckState.NotApplicable
		);

		const [isBusy, setIsBusy] = useState<boolean>(false);
		const [selectMode, setSelectMode] = useState<SelectMode>(SelectMode.Disabled);
		const [selectedItems, setSelectedItems] = useState<readonly EmailActionModel[]>(EMPTY_ARRAY);
		const [itemBeingViewed, setItemBeingViewed] = useState<EmailActionModel | null>(null);
		const [isAttachmentUploadInProgress, setIsAttachmentUploadInProgress] = useState<boolean>(false);
		const [composerHandle, setComposerHandle] = useState<EmailComposerHandle | null>(null);

		const [signatureSettingsDialog, openSignatureSettingsDialog] = useSignatureSettingsDialog();
		const userTimezone: TimeZone = useUserTimezone();

		const [uploadAttachmentInProgressDialog, openUploadAttachmentInProgressDialog] = useUploadAttachmentInProgressDialog();
		const [moveEmailsToFolderDialog, openMoveEmailsToFolderDialog] = useMoveEmailsToFolderDialog();

		const signatures: readonly SignatureModel[] = useSignatureStore(x => x.signatures);
		const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);
		const globalCancellation: AbortSignal = useUnmountedCancellation();

		const listViewRef: RefObject<ListViewHandle | null> = useRef<ListViewHandle>(null);
		const alertManager: AlertManager = useAlerts();
		const didMarkViewedEmailAsUnreadRef: RefObject<boolean> = useRef<boolean>(false);
		const didDeleteViewedEmailRef: RefObject<boolean> = useRef<boolean>(false);
		const selectedFolder: (IFolderViewModel<LazyLoadedChildren>) | undefined = selectedFolderWithMailbox?.folder;

		useEffect(
			() => {
				switch (integrationCredentialsCheckState) {
					case IntegrationCredentialsCheckState.CheckIsRequired:
						offerPasswordUpdateWhenPossibleChanged({ kind: IntegrationCredentialsLinkKind.Exchange })
							.then(result => {
								setIntegrationCredentialsCheckState(
									result
										? IntegrationCredentialsCheckState.ShouldReloadCredentials
										: IntegrationCredentialsCheckState.CredentialsAreGood
								);
							})
							.catch(_ => {
								setIntegrationCredentialsCheckState(IntegrationCredentialsCheckState.Error);
							});
						break;
					default:
						break;
				}
			},
			[integrationCredentialsCheckState]
		);

		const composeNewEmailHandler = useCallback(
			(): void => {
				setSelectedItems(EMPTY_ARRAY);
				onComposeNewEmail();
			},
			[onComposeNewEmail]
		);

		const selectionContext = useMemo(
			(): EmailSelectionActionContext => {
				const effectiveSelection: readonly EmailActionModel[] = selectedItems.length > 0
					? selectedItems
					: isNotNil(itemBeingViewed)
						? [itemBeingViewed]
						: EMPTY_ARRAY;

				return {
					selectedItems,
					effectiveSelection
				};
			},
			[itemBeingViewed, selectedItems]
		);

		const setFolderCounters = useCallback(
			(folderCounters: FolderCounters): void => {
				return setCounters(
					(prevState) => {
						const existing: FolderCounters | undefined = prevState.get(
							folderCounters.folderId
								.id
						);

						if (existing
							&& existing.total === folderCounters.total
							&& existing.unread === folderCounters.unread
						) {
							return prevState;
						}

						const newCounters: Map<string, FolderCounters> = new Map(prevState);
						newCounters.set(
							folderCounters.folderId
								.id,
							folderCounters
						);

						return newCounters;
					}
				);
			},
			[]
		);

		const addDeltaCounters = useCallback(
			(
				folderId: string,
				deltas: EmailCounterDeltas
			): void => {
				setCounters(
					(prevState) => {
						const newCounters: Map<string, FolderCounters> = new Map(prevState);
						const data: FolderCounters | undefined = newCounters.get(folderId);

						if (!data) {
							newCounters.set(
								folderId,
								{
									folderId: { id: folderId },
									unread: Math.max(
										0,
										deltas.unreadDelta
									),
									total: Math.max(
										0,
										deltas.totalDelta
									)
								}
							);
						} else {
							newCounters.set(
								folderId,
								{
									folderId: { id: folderId },
									unread: Math.max(
										0,
										data.unread + deltas.unreadDelta
									),
									total: Math.max(
										0,
										data.total + deltas.totalDelta
									)
								}
							);
						}
						return newCounters;
					}
				);
			},
			[]
		);

		const subtractDeltaCounters = useCallback(
			(
				folderId: string,
				deltas: EmailCounterDeltas
			): void => {
				setCounters(
					(prevState) => {
						const newCounters: Map<string, FolderCounters> = new Map(prevState);
						const data: FolderCounters | undefined = newCounters.get(folderId);

						if (data) {
							newCounters.set(
								folderId,
								{
									folderId: { id: folderId },
									unread: Math.max(
										0,
										data.unread - deltas.unreadDelta
									),
									total: Math.max(
										0,
										data.total - deltas.totalDelta
									)
								}
							);
						}
						return newCounters;
					}
				);
			},
			[]
		);

		const setCountersRecursively = useCallback(
			(folderItem: ExchangeFolderItem): void => {
				const { folder } = folderItem;

				setFolderCounters({
					folderId: folder.folderId,
					total: folder.totalCount,
					unread: folder.unreadCount
				});

				if (folderItem.childrenState.status !== "success") {
					return;
				}

				const subFolders: readonly ExchangeFolderItem[] = folderItem
					.childrenState
					.children;

				for (const subFolder of subFolders) {
					setCountersRecursively(subFolder);
				}
			},
			[setFolderCounters]
		);

		useEffect(
			() => {
				for (const accountItem of accountItems) {
					if (accountItem.isFaulted) {
						continue;
					}

					for (const folderItem of accountItem.folders) {
						setCountersRecursively(folderItem);
					}
				}
			},
			[
				accountItems,
				setCountersRecursively
			]
		);

		const isExchangeConfigured: boolean = useMemo(
			() => {
				return userConfiguration()
					.exchange
					.isConfigured;
			},
			[]
		);

		const readStatusChangedHandler = useCallback(
			(data: ReadStatusChangedData) => {
				setCounters(
					(prevState) => {
						const { folderId } = data;
						const folderCounters: FolderCounters | undefined = prevState.get(folderId);
						if (!folderCounters) {
							return prevState;
						}

						let { total, unread } = folderCounters;
						const isRead: boolean = data.state === MessageReadState.Read;

					switch (data.kind) {
						case "all":
							unread = isRead ? 0 : total;
							break;
						case "specific":
							const { emailIds } = data;
							unread = isRead
								? Math.max(0, unread - emailIds.length)
								: Math.min(total, unread + emailIds.length);
							break;
						default:
							throw new UnreachableCaseError(data);
					}

					const newCounters: Map<string, FolderCounters> = new Map(prevState);
					newCounters.set(
						folderCounters.folderId
							.id,
						folderCounters
					);

						return newCounters;
					}
				);
			},
			[]
		);

		const emailChangeHandler = useCallback(
			(emailId: ExchangeEmailId, changes: { isRead?: boolean; isFlagged?: boolean; }): void => {
				if (sessionState.kind !== EmailSessionStateKind.ViewEmail) {
					return;
				}

				if (sessionState.emailId.id !== emailId.id) {
					return;
				}

				if (changes.isRead === false) {
					didMarkViewedEmailAsUnreadRef.current = true;
				}

				setItemBeingViewed(
					itemBeingViewed => {
						if (!itemBeingViewed) {
							return itemBeingViewed;
						}

						if (itemBeingViewed.emailId.id !== emailId.id) {
							return itemBeingViewed;
						}

						const copy: EmailActionModel = {
							...itemBeingViewed,
							emailId
						};

						if (isNotNil(changes.isRead)) {
							copy.isRead = changes.isRead;
						}

						if (isNotNil(changes.isFlagged)) {
							copy.isFlagged = changes.isFlagged;
						}

						return copy;
					}
				);
			},
			[sessionState]
		);

		const readStatusChangeHandler = useCallback(
			async (
				emailIds: readonly ExchangeEmailId[],
				readStatus: MessageReadState,
				smtpAddress: string | null
			): Promise<void> => {
				if (selectedFolder) {
					readStatusChangedHandler({
						kind: "specific",
						folderId: selectedFolder
							.folderId
							.id,
						emailIds,
						state: readStatus
					});
				}

				listViewRef.current
					?.changeReadStateOfItems(emailIds, readStatus);

				for (const emailId of emailIds) {
					const isRead: boolean = readStatus === MessageReadState.Read;

					emailChangeHandler(
						emailId,
						{ isRead }
					);
					publish(
						EmailEvents.emailReadStatusChanged,
						{ emailId, isRead }
					);
				}

				await setExchangeReadStatus(
					{
						smtpAddress: isNotNil(smtpAddress)
							? { isSet: true, value: smtpAddress }
							: { isSet: false },
						emailIds
					},
					readStatus
				);
			},
			[
				selectedFolder,
				readStatusChangedHandler,
				emailChangeHandler
			]
		);

		const flagStatusChangeHandler = useCallback(
			async (
				emailIds: readonly ExchangeEmailId[],
				flagStatus: MessageFlagState,
				smtpAddress: string | null
			): Promise<void> => {
				listViewRef.current
					?.changeFlagStateOfItems(emailIds, flagStatus);

				for (const emailId of emailIds) {
					const isFlagged: boolean = flagStatus === MessageFlagState.Flagged;

					emailChangeHandler(
						emailId,
						{ isFlagged }
					);
					publish(
						EmailEvents.emailFlagStatusChanged,
						{ emailId, isFlagged }
					);
				}

				await setExchangeFlagStatus(
					{
						smtpAddress: isNotNil(smtpAddress)
							? { isSet: true, value: smtpAddress }
							: { isSet: false },
						emailIds
					},
					flagStatus
				);
			},
			[emailChangeHandler]
		);

		const notifyAboutChangedReadStatusOfAFolder = useCallback(
			(folderId: string, readStatus: MessageReadState): void => {
				readStatusChangedHandler(
					{
						folderId,
						state: readStatus,
						kind: "all",
					}
				);
			},
			[readStatusChangedHandler]
		);

		const manageMailboxesHandler = useCallback(
			(): void => {
				setIsMailboxesManagementSidebarVisible(true);
			},
			[]
		);

		const inboxFolderLocations: MailFolderLocationModel[] = useMemo(
			() => {
				return accountItems
					.map((x): MailFolderLocationModel | null => {
						const inboxFolderInAccount: IFolderViewModel<LazyLoadedChildren> | undefined = x
							.account
							.folders
							.find((y) => y.isInbox);

						if (typeof inboxFolderInAccount === "undefined") {
							return null;
						}

						return {
							smtpAddress: {
								isSet: true,
								value: x.account.emailAddress
							},
							folderId: inboxFolderInAccount.folderId.id
						};
					})
					.filter(isNotNil);
			},
			[accountItems]
		);

		useEffect(
			() => {
				const fetchUpdates = async (signal: AbortSignal): Promise<void> => {
					if (!selectedFolderWithMailbox) {
						return;
					}

					const {
						account,
						folder
					} = selectedFolderWithMailbox;

					const selectedFolderLocation: MailFolderLocationModel = {
						smtpAddress: {
							isSet: true,
							value: account.emailAddress
						},
						folderId: folder.folderId.id
					};

					const counterRequests: MailFolderLocationModel[] = [...inboxFolderLocations];
					const isCurrentFolderAlreadyPresent: boolean = counterRequests.some(
						f => f.folderId === selectedFolderLocation.folderId
							&& f.smtpAddress.isSet
							&& f.smtpAddress.value === account.emailAddress
					);
					if (!isCurrentFolderAlreadyPresent) {
						counterRequests.push(selectedFolderLocation);
					}

					const request: GetEmailChangesRequest = {
						countersRequests: counterRequests,
						newMessageRequest: {
							folder: selectedFolderLocation,
							watermark: watermarkRef.current
						}
					};

					try {
						const {
							counters,
							emailChanges
						} = await getNewEmailsForFolderFromExchange(request, signal);

						for (const folderCounters of counters) {
							setFolderCounters(folderCounters);
						}

						if (request.newMessageRequest.watermark !== watermarkRef.current) {
							return;
						}

						if (emailChanges) {
							listViewRef.current
								?.processUpdate(emailChanges.changes);

							watermarkRef.current = emailChanges.watermark;
						}
					} catch (e) {
						if (isCancelledError(e)) {
							return;
						}

						throw e;
					}
				};

				const poller = new Poller({
					intervalInSeconds: millisecondsBetweenRetrievingEmailChanges / 1000,
					callback: fetchUpdates,
					shouldRemoveMinimalIntervalConstraint: true,
					shouldInvokeImmediately: false
				});

				poller.start();

				return () => poller.destroy();
			},
			[
				selectedFolderWithMailbox,
				inboxFolderLocations,
				millisecondsBetweenRetrievingEmailChanges,
				setFolderCounters
			]
		);

		const watermarkChangeHandler = useCallback(
			(newValue: string): void => {
				watermarkRef.current = newValue;
			},
			[]
		);

		const notifyAboutMovedEmails = useCallback(
			(
				emails: readonly EmailIdWithIsReadStatus[],
				destinationFolderId: ExchangeFolderId
			): void => {
				const deltas: EmailCounterDeltas = getEmailsDeltas(emails);

				const {
					folder
				} = ensureNotNil(
					selectedFolderWithMailbox,
					"No folder was selected but emails were moved"
				);

				const sourceFolderId: string = folder.folderId.id;

				subtractDeltaCounters(
					sourceFolderId,
					deltas
				);

				addDeltaCounters(
					destinationFolderId.id,
					deltas
				);

				if (destinationFolderId.id === sourceFolderId) {
					listViewRef.current
						?.refresh();
				} else {
					listViewRef.current
						?.removeItems(emails.map(e => e.emailId));
				}

				if (sessionState.kind === EmailSessionStateKind.ViewEmail
					|| sessionState.kind === EmailSessionStateKind.EditDraft
				) {
					const hasOpenedEmailBeenRemoved: boolean = emails.some(e => e.emailId.id === sessionState.emailId.id);

					if (hasOpenedEmailBeenRemoved) {
						onSwitchToNoSession();
					}
				}
			},
			[
				addDeltaCounters,
				selectedFolderWithMailbox,
				sessionState,
				subtractDeltaCounters,
				onSwitchToNoSession
			]
		);

		const moveEmailsToFolderHandler = useCallback(
			async (
				destinationFolder: IFolderViewModel<LazyLoadedChildren>,
				emails: readonly EmailIdWithIsReadStatus[],
				smtpAddress: string | null
			): Promise<void> => {
				try {
					await moveEmailToFolder({
						selection: {
							smtpAddress: isNotNil(smtpAddress)
								? { isSet: true, value: smtpAddress }
								: { isSet: false },
							emailIds: emails.map(e => e.emailId)
						},
						destinationFolderId: destinationFolder.folderId
					});

					alertManager.showSuccess(
						(
							<FormattedText
								text={localize("_EmailsSuccessfullyMovedToFolderName")}
								thingsToReplace={[
									{ what: "{{FolderName}}", filler: destinationFolder.name }
								]}
							/>
						)
					);

					notifyAboutMovedEmails(emails, destinationFolder.folderId);
				} catch (e) {
					let handled = false;
					if (e instanceof HttpRequestError) {
						try {
							const responseData: ExchangeEmailManagementErrorModel = e.readJson<ExchangeEmailManagementErrorModel>();
							alertManager.showError(responseData.message);
							handled = true;
						} catch (parseError) {
							console.error(parseError);
						}
					}

					if (!handled) {
						alertManager.showError(localize("_GeneralErrorMessage"));
						console.error(e);
					}
				}
			},
			[
				alertManager,
				notifyAboutMovedEmails
			]
		);

		const respondToEmailHandler = useCallback(
			async (
				emailId: ExchangeEmailId,
				kind: EmailResponseKind,
				smtpAddress: string | null
			): Promise<void> => {

				onSwitchToNoSession();
				setIsBusy(true);

				const source: MailMessageLocationQueryModel = {
					smtpAddress,
					messageId: emailId.id
				};

				try {
					const utcOffset: string = formatForDto(new Date(), userTimezone);
					let draftModel: DraftEmailModel;
					switch (kind) {
						case EmailResponseKind.Reply:
							draftModel = await prepareExchangeEmailForReply(source, utcOffset);
							break;
						case EmailResponseKind.ReplyAll:
							draftModel = await prepareExchangeEmailForReplyAll(source, utcOffset);
							break;
						case EmailResponseKind.Forward:
							draftModel = await prepareExchangeEmailForForward(source, utcOffset);
							break;
						default:
							throw never(kind);
					}

					onOpenResponseDraft(
						draftModel.exchangeId.id,
						kind,
						emailId,
						smtpAddress
					);
					setIsBusy(false);
				} catch(e) {
					if (isCancelledError(e)) {
						return;
					}

					setIsBusy(false);
				}
			},
			[
				onOpenResponseDraft,
				onSwitchToNoSession,
				userTimezone
			]
		);

		const afterSendResponse = useCallback(
			(response: EmailResponseSessionState): void => {
				switch (response.kind) {
					case EmailResponseKind.Reply:
					case EmailResponseKind.ReplyAll:
						listViewRef.current
							?.makeAwareAboutLastActionChange(response.originalEmailId, EmailActions.Replied);
						break;
					case EmailResponseKind.Forward:
						listViewRef.current
							?.makeAwareAboutLastActionChange(response.originalEmailId, EmailActions.Forwarded);
						break;
					default:
						never(response.kind);
				}
			},
			[]
		);

		const pickDestinationAndMoveEmailsHandler = useCallback(
			async (
				emails: readonly EmailIdWithIsReadStatus[],
				smtpAddress: string | null
			): Promise<void> => {
				if (!emails || emails.length === 0) {
					return;
				}

				let didChangeFolderStructure: boolean = false;

				const moveResult: TryResult<FolderMovedData> = await openMoveEmailsToFolderDialog({
					emails,
					currentFolderId: selectedFolder
						?.folderId
						.id,
					sourceSmtpAddress: smtpAddress,
					onFolderStructureChange: (): void => {
						didChangeFolderStructure = true;
					}
				});

				if (moveResult.wasSuccessful) {
					const {
						folderName,
						destinationFolderId
					} = moveResult.result;

					alertManager.showSuccess(
						(
							<FormattedText
								text={localize("_EmailsSuccessfullyMovedToFolderName")}
								thingsToReplace={[
									{ what: "{{FolderName}}", filler: folderName }
								]}
							/>
						)
					);

					notifyAboutMovedEmails(emails, { id: destinationFolderId });
				}

				if (didChangeFolderStructure) {
					onReloadScreenModel();
				}
			},
			[
				alertManager,
				notifyAboutMovedEmails,
				selectedFolder,
				openMoveEmailsToFolderDialog,
				onReloadScreenModel
			]
		);

		const emailDeletedChangeHandler = useCallback(
			(emails: readonly EmailIdWithIsReadStatus[]) => {
				const deltas: EmailCounterDeltas = getEmailsDeltas(emails);

				const {
					folder
				} = ensureNotNil(
					selectedFolderWithMailbox,
					"No folder was selected but emails were deleted"
				);

				subtractDeltaCounters(
					folder.folderId.id,
					deltas
				);
			},
			[selectedFolderWithMailbox, subtractDeltaCounters]
		);


		useEffect(
			() => {
				const tokenSubscription: SubscriptionTokenCollection = new SubscriptionTokenCollection();

				const subscription = subscribe(
					EmailEvents.emailsDeleted,
					(_, data) => {
						emailDeletedChangeHandler(data.emails);
					}
				);

				tokenSubscription.add(subscription);

				return () => {
					tokenSubscription.destroy();
				};
			},
			[emailDeletedChangeHandler]
		);

		const isAutoMarkAsReadEnabled: boolean = selectedFolderWithMailbox
			?.account
			?.isAutoMarkAsReadEnabled ?? false;

		useEffect(
			(): (() => void) | void => {
				if (sessionState.kind !== EmailSessionStateKind.ViewEmail
					|| !isAutoMarkAsReadEnabled
				) {
					return;
				}

				return (): void => {
					if (didMarkViewedEmailAsUnreadRef.current
						|| didDeleteViewedEmailRef.current
					) {
						return;
					}

					readStatusChangeHandler(
						[sessionState.emailId],
						MessageReadState.Read,
						sessionState.smtpAddress
					).catch(
						(e) => {
							console.error("Failed to mark viewed email as read", e);
						}
					);
				};
			},
			[
				sessionState,
				isAutoMarkAsReadEnabled,
				readStatusChangeHandler
			]
		);

		useEffect(
			(): EffectWithDestructorResult => {
				if (sessionState.kind !== EmailSessionStateKind.ViewEmail) {
					return;
				}

				return (): void => {
					didMarkViewedEmailAsUnreadRef.current = false;
					didDeleteViewedEmailRef.current = false;
				};
			},
			[sessionState]
		);

		useEffect(
			(): void => {
				if (!isMobile && selectedItems.length > 1) {
					onSwitchToNoSession();
				}
			},
			[
				onSwitchToNoSession,
				selectedItems.length,
				isMobile
			]
		);

		const notifyAboutDeletedEmailsHandler = useCallback(
			(emails: readonly EmailIdWithIsReadStatus[]): void => {
				emailDeletedChangeHandler(emails);

				const emailIds: ExchangeEmailId[] = emails.map((x) => x.emailId);

				if (sessionState.kind === EmailSessionStateKind.ViewEmail
					|| (
						sessionState.kind === EmailSessionStateKind.EditDraft
							&& !sessionState.response
					)
				) {
					const hasOpenedEmailBeenRemoved: boolean = emailIds.some(e => e.id === sessionState.emailId.id);

					if (hasOpenedEmailBeenRemoved) {
						didDeleteViewedEmailRef.current = true;
						let emailToOpenNext: EmailListItemModel | undefined;
						if (!isMobile) {
							emailToOpenNext = listViewRef.current
								?.getNextEmail(sessionState.emailId, emailIds);
						}

						if (emailToOpenNext) {
							onOpenEmail(
								emailToOpenNext.emailId
									.id,
								emailToOpenNext.isDraft
							);
						} else {
							onSwitchToNoSession();
						}
					}
				}

				listViewRef.current
					?.removeItems(emailIds);
			},
			[
				isMobile,
				emailDeletedChangeHandler,
				onOpenEmail,
				onSwitchToNoSession,
				sessionState
			]
		);

		const notifyAboutCanceledEmailHandler = useCallback(
			(): void => {
				if (sessionState.kind === EmailSessionStateKind.EditDraft
					&& isNotNil(sessionState.response)
				) {
					onOpenEmail(
						sessionState.response.originalEmailId.id,
						false
					);
				} else {
					onSwitchToNoSession();
				}
			},
			[
				onOpenEmail,
				onSwitchToNoSession,
				sessionState
			]
		);

		const deleteEmailsFromCurrentFolderHandler = useCallback(
			(
				emails: readonly EmailIdWithIsReadStatus[],
				smtpAddress: string | null
			): void => {
				if (!emails || emails.length === 0) {
					return;
				}

				if (isNil(selectedFolderWithMailbox)) {
					console.error("There is no current folder to delete e-mails from.");
					return;
				}

				const {
					folder: selectedFolder
				} = selectedFolderWithMailbox;

				const softDelete: boolean = !selectedFolder.isDeleted;
				deleteExchangeEmailByIds(
					{
						smtpAddress: isNotNil(smtpAddress)
							? { isSet: true, value: smtpAddress }
							: { isSet: false },
						emailIds: emails.map((x) => x.emailId)
					},
					softDelete
				);

				notifyAboutDeletedEmailsHandler(emails);
			},
			[notifyAboutDeletedEmailsHandler, selectedFolderWithMailbox]
		);

		const deleteSingleEmailHandler = useCallback(
			async (
				emailId: ExchangeEmailId,
				smtpAddress: string | null,
				parentFolderId: string | null
			): Promise<void> => {
				let isUnderDeleteFolder: boolean = false;

				if (isNotNil(parentFolderId) && isNotNil(smtpAddress)) {
					const updatedAccountItems: readonly ExchangeAccountItem[] = await onFetchFolders(
						smtpAddress,
						parentFolderId,
						true
					);

					isUnderDeleteFolder = isFolderUnderDeleteFolder(
						updatedAccountItems,
						smtpAddress,
						parentFolderId
					);
				}

				deleteExchangeEmailByIds(
					{
						smtpAddress: isNotNil(smtpAddress)
							? {
								isSet: true,
								value: smtpAddress
							}
							: { isSet: false },
						emailIds: [emailId]
					},
					!isUnderDeleteFolder
				);

				notifyAboutDeletedEmailsHandler([{ emailId, isRead: false }]);
			},
			[
				notifyAboutDeletedEmailsHandler,
				onFetchFolders
			]
		);

		const folderReadStatusChangeHandler = useCallback(
			async (
				smtpAddress: string,
				folderId: string,
				readStatus: MessageReadState
			): Promise<void> => {
				try {
					switch (readStatus) {
						case MessageReadState.Read:
							await markAllAsRead(
								{
									smtpAddress: {
										isSet: true,
										value: smtpAddress
									},
									folderId
								},
								globalCancellation
							);
							break;
						case MessageReadState.Unread:
							await markAllAsUnread(
								{
									smtpAddress: {
										isSet: true,
										value: smtpAddress
									},
									folderId
								},
								globalCancellation
							);
							break;
						default:
							never(readStatus);
					}

					notifyAboutChangedReadStatusOfAFolder(
						folderId,
						readStatus
					);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
					throw e;
				}
			},
			[
				alertManager,
				notifyAboutChangedReadStatusOfAFolder,
				globalCancellation
			]
		);

		const emailActionBarOptions = useMemo(
			(): EmailActionBarOptions => {
				return {
					hasSession: sessionState.kind !== EmailSessionStateKind.None,
					showGoToOutlookAction,
					composeEmailActionOptions: {
						hasSendAction: true,
						hasDiscardAction: true,
						signatures,
						callbacks: {
							onIsInteractingWithBackendChange: setIsBusy,
							onOpenSignatureSettingsDialog: openSignatureSettingsDialog,
						}
					},
					selectMode,
					selectionContext,
					isAttachmentUploadInProgress,
					composerHandle,
					onChangeFlagStatus: flagStatusChangeHandler,
					onChangeReadStatus: readStatusChangeHandler,
					onComposeNewEmail: composeNewEmailHandler,
					onDeleteEmails: deleteEmailsFromCurrentFolderHandler,
					onMoveEmails: pickDestinationAndMoveEmailsHandler,
					onRespondToEmail: respondToEmailHandler,
					onChangeSelectAll: (selectAll: boolean) => {
						listViewRef.current
							?.changeSelectAll(selectAll);
					},
					onSelectModeChange: setSelectMode,
					onShowUploadAttachmentInProgressError: openUploadAttachmentInProgressDialog
				};
			},
			[
				flagStatusChangeHandler,
				readStatusChangeHandler,
				deleteEmailsFromCurrentFolderHandler,
				composeNewEmailHandler,
				openSignatureSettingsDialog,
				pickDestinationAndMoveEmailsHandler,
				respondToEmailHandler,
				sessionState.kind,
				showGoToOutlookAction,
				signatures,
				selectMode,
				selectionContext,
				isAttachmentUploadInProgress,
				openUploadAttachmentInProgressDialog,
				composerHandle
			]
		);

		const actionsWithContext: ActionsWithContext<CompositeActionContext> = useEmailActionBar(emailActionBarOptions);

		useImperativeHandle(
			ref,
			(): EmailPageHandle => (
				{
					notifyAboutDeletedEmails: notifyAboutDeletedEmailsHandler
				}
			),
			[notifyAboutDeletedEmailsHandler]
		);

		const clearSelectionHandler = useCallback(
			() => {
				listViewRef.current
					?.clearSelection();
			},
			[]
		);

		const areIntegrationCredentialsValid: boolean = (
			integrationCredentialsCheckState === IntegrationCredentialsCheckState.NotApplicable ||
			integrationCredentialsCheckState === IntegrationCredentialsCheckState.CredentialsAreGood
		);

		const canRenderContent: boolean = isExchangeConfigured
			&& areIntegrationCredentialsValid;

		const selectedFolderCounters = useMemo(
			(): FolderCounters | undefined => {
				return selectedFolder
					? counters.get(
						selectedFolder
							.folderId
							.id
					)
					: undefined;
			},
			[
				counters,
				selectedFolder
			]
		);

		const closeMailboxManagementSidebarHandler = useCallback(
			(): void => setIsMailboxesManagementSidebarVisible(false),
			[]
		);

		return (
			<div className={classNames(
				styles.root,
				{ [styles.root_hasSession]: sessionState.kind !== EmailSessionStateKind.None }
			)}>
				{isBusy && (
					<BusyIndicator />
				)}
				{canRenderContent && (
					<>
						<EmailSidebar
							onManageMailboxes={manageMailboxesHandler}
						>
							{
								(onIsLoadingChange, onSidebarNavigation) => (
									<EmailTreeView
										accountItems={accountItems}
										onAccountsChange={onAccountsChange}
										onReadStatusChange={folderReadStatusChangeHandler}
										onMoveEmailsToFolder={moveEmailsToFolderHandler}
										selectedFolderWithMailbox={selectedFolderWithMailbox}
										counters={counters}
										doesSupportContextActions
										setFolderCounters={setFolderCounters}
										onNavigateToFolder={onNavigateToFolder}
										onIsLoadingChange={onIsLoadingChange}
										onSidebarNavigation={onSidebarNavigation}
										onFetchFolders={onFetchFolders}
									/>
								)
							}
						</EmailSidebar>

						<div className={styles.actionbar}>
							<ContextActionBar
								{...actionsWithContext}
								trackingId="global-action-bar"
							/>
						</div>

						<div {...setTrackingId("email-list")} className={styles.emailList}>
							{selectedFolderWithMailbox && (
								<ListView
									folder={selectedFolderWithMailbox.folder}
									smtpAddress={
										selectedFolderWithMailbox
											.account
											.emailAddress
									}
									emailSession={sessionState}
									isMobile={isMobile}
									onWatermarkChange={watermarkChangeHandler}
									ref={listViewRef}
									onEmailsChange={setCanShowEmptyMessage}
									onOpenEmail={onOpenEmail}
									counters={selectedFolderCounters}
									onFolderCountersRetrieved={setFolderCounters}
									onComposeNewEmail={composeNewEmailHandler}
									selectMode={selectMode}
									selectedItems={selectedItems}
									onSelectModeChange={setSelectMode}
									onSelectionChange={setSelectedItems}
									isAttachmentUploadInProgress={isAttachmentUploadInProgress}
									onDeleteEmails={deleteEmailsFromCurrentFolderHandler}
									onFlagStatusChange={flagStatusChangeHandler}
									onReadStatusChange={readStatusChangeHandler}
									onMoveEmails={pickDestinationAndMoveEmailsHandler}
									onRespondToEmail={respondToEmailHandler}
									onFolderReadStatusChange={folderReadStatusChangeHandler}
									timezone={userTimezone}
								/>
							)}
							{uploadAttachmentInProgressDialog}
						</div>

						<div className={styles.emailContent}>
							<EmailContent
								emailSession={sessionState}
								smtpAddress={
									selectedFolderWithMailbox
										?.account
										.emailAddress ?? null
								}
								onDeleteEmails={deleteEmailsFromCurrentFolderHandler}
								onDeleteSingleEmail={deleteSingleEmailHandler}
								onNotifyAboutDeletedEmails={notifyAboutDeletedEmailsHandler}
								defaultComposerFontStyle={defaultComposerFontStyle}
								shouldAutomaticallyShowSenderImages={automaticallyShowSenderImages}
								availableFroms={availableFroms}
								onSwitchToNoSession={onSwitchToNoSession}
								onOpenEmail={onOpenEmail}
								onOpenFolder={onOpenFolder}
								shouldShowEmptyMessage={canShowEmptyMessage}
								afterSendResponse={afterSendResponse}
								onRespondToEmail={respondToEmailHandler}
								onFlagStatusChange={flagStatusChangeHandler}
								onReadStatusChange={readStatusChangeHandler}
								onMoveEmails={pickDestinationAndMoveEmailsHandler}
								onClearSelection={clearSelectionHandler}
								selectionContext={selectionContext}
								onEmailBeingViewedChange={setItemBeingViewed}
								isAttachmentUploadInProgress={isAttachmentUploadInProgress}
								onIsAttachmentUploadInProgressChange={setIsAttachmentUploadInProgress}
								composerRef={setComposerHandle}
								timezone={userTimezone}
								onNotifyAboutCanceledEmail={notifyAboutCanceledEmailHandler}
							/>
						</div>
						{sessionState.kind === EmailSessionStateKind.None && (
							<FloatingActionButton
								icon="plus"
								onClick={composeNewEmailHandler}
								trackingId="fab-new-email"
							/>
						)}
					</>
				)}
				{!isExchangeConfigured && (
					<EmailNotConfiguredDialog shouldNavigateBackToWorkspaceOnClose />
				)}
				{isMailboxesManagementSidebarVisible && (
					<MailboxesManagementSidebar
						onSave={onReloadScreenModel}
						onClose={closeMailboxManagementSidebarHandler}
					/>
				)}
				{signatureSettingsDialog}
				{moveEmailsToFolderDialog}
			</div>
		);
	}
);

EmailPage.displayName = "EmailPage";

export enum EmailResponseKind {
	Reply = 0,
	ReplyAll = 1,
	Forward = 2
}

enum IntegrationCredentialsCheckState {
	NotApplicable = 0,
	CheckIsRequired = 1,
	CredentialsAreGood = 2,
	ShouldReloadCredentials = 3,
	Error = 4
}

type EmailCounterDeltas = {
	unreadDelta: number;
	totalDelta: number;
};

type ReadStatusChangedData = { folderId: string; state: MessageReadState; } & ({ kind: "all"; } | { kind: "specific"; emailIds: readonly ExchangeEmailId[]; });

function getEmailsDeltas(emails: readonly EmailIdWithIsReadStatus[]): EmailCounterDeltas {
	const totalDelta: number = emails.length;
	const unreadDelta: number = emails.filter((x) => !x.isRead).length;

	return {
		totalDelta: totalDelta,
		unreadDelta: unreadDelta
	};
}

